/**
 * TraditionsSearch — typeahead search bar at the top of the Traditions index.
 *
 * Behaviour mirrors the Compare autocomplete in TraditionDetail (consistent
 * pattern across the lexicon): prefix-first ranking, ghost-text completion,
 * keyboard nav (↓↑ Enter Esc, Tab to accept ghost), mouse click to select.
 *
 * On select, navigates to /traditions/<Name-With-Dashes>. The user's typed
 * query is cleared so the next visit starts fresh.
 */
import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { TRADITIONS } from "../lib/traditions";
import { traditionPath } from "../pages/Traditions";

// Bold the matched substring inside a suggestion label.
function highlightMatch(label, query) {
  const q = (query || "").trim();
  if (!q) return label;
  const idx = label.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return label;
  return (
    <>
      {label.slice(0, idx)}
      <span className="font-semibold text-[hsl(var(--primary))]">
        {label.slice(idx, idx + q.length)}
      </span>
      {label.slice(idx + q.length)}
    </>
  );
}

export default function TraditionsSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  // Ranked autocomplete: prefix > substring. Cap at 8 so the dropdown stays calm.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TRADITIONS.slice(0, 8);
    const starts = [];
    const contains = [];
    for (const t of TRADITIONS) {
      const ln = t.name.toLowerCase();
      if (ln.startsWith(q)) starts.push(t);
      else if (ln.includes(q)) contains.push(t);
    }
    return [...starts, ...contains].slice(0, 8);
  }, [query]);

  // Ghost-text completion: the first prefix-matching suggestion's tail.
  const ghost = useMemo(() => {
    if (!query) return "";
    const head = suggestions[0]?.name;
    if (!head) return "";
    if (head.toLowerCase().startsWith(query.toLowerCase()) && head.length > query.length) {
      return head.slice(query.length);
    }
    return "";
  }, [query, suggestions]);

  // Clamp highlight if the list shrinks.
  useEffect(() => {
    if (highlight >= suggestions.length) setHighlight(0);
  }, [suggestions.length, highlight]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const choose = (tradition) => {
    setOpen(false);
    setQuery("");
    navigate(traditionPath(tradition));
  };

  const onKey = (e) => {
    // Tab / Right-arrow at end → accept ghost-text completion.
    if (ghost && (e.key === "Tab" || (e.key === "ArrowRight" && e.target.selectionStart === query.length))) {
      e.preventDefault();
      if (suggestions[0]) setQuery(suggestions[0].name);
      else setQuery(query + ghost);
      return;
    }
    if (!open) {
      if (e.key === "ArrowDown") {
        setOpen(true);
        setHighlight(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % Math.max(1, suggestions.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % Math.max(1, suggestions.length));
    } else if (e.key === "Enter") {
      if (suggestions[highlight]) {
        e.preventDefault();
        choose(suggestions[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const clear = () => {
    setQuery("");
    setOpen(true);
    inputRef.current?.focus();
  };

  return (
    <div
      ref={boxRef}
      data-testid="traditions-search"
      className="relative max-w-xl"
    >
      {/* Ghost-text completion sits behind the real input. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center pl-10 text-[14px] mono whitespace-pre overflow-hidden"
      >
        <span className="invisible">{query}</span>
        <span className="text-muted-foreground/45" data-testid="traditions-search-ghost">{ghost}</span>
      </div>

      <Search
        size={16}
        strokeWidth={1.7}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
      />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Search traditions…"
        data-testid="traditions-search-input"
        className="input-line w-full text-[14px] mono bg-transparent pl-10 pr-9 relative"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls="traditions-search-suggestions"
      />
      {query && (
        <button
          type="button"
          onClick={clear}
          data-testid="traditions-search-clear"
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center text-muted-foreground hover:text-foreground"
        >
          <X size={14} strokeWidth={1.7} />
        </button>
      )}

      {open && suggestions.length > 0 && (
        <ul
          id="traditions-search-suggestions"
          role="listbox"
          data-testid="traditions-search-suggestions"
          className="absolute z-30 left-0 right-0 top-full mt-1 max-h-80 overflow-auto bg-background border border-border shadow-lg rounded-md py-1"
        >
          {suggestions.map((t, i) => (
            <li
              key={t.slug}
              role="option"
              aria-selected={i === highlight}
              data-testid={`traditions-search-suggestion-${t.slug}`}
              onMouseDown={(e) => { e.preventDefault(); choose(t); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-3 ${
                i === highlight
                  ? "bg-[hsl(var(--primary)/0.10)] text-[hsl(var(--primary))]"
                  : "text-foreground/85 hover:bg-secondary/60"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="serif text-[15px] truncate">
                  {highlightMatch(t.name, query)}
                </div>
                <div className="mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/65 truncate">
                  {t.era}
                </div>
              </div>
              {i === highlight && (
                <span className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground/70 flex-shrink-0">↵</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {ghost && (
        <p className="mt-2 mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/65">
          Tab to complete · ↵ to jump · ↓ to browse
        </p>
      )}
    </div>
  );
}
